import {
    PlusOutlined,
    EyeOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
    DollarOutlined,
    FileTextOutlined,
} from '@ant-design/icons';
import {
    ConfigProvider,
    DatePicker,
    Card,
    Table,
    Tag,
    Space,
    Button,
    Row,
    Col,
    Statistic,
    Modal,
    Form,
    InputNumber,
    Divider,
    Alert,
    Descriptions,
    Spin,
} from 'antd';
import frFR from 'antd/locale/fr_FR';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import Footer from '../../../components/Footer';
import NavBar from '../../../components/NavBar';
import SideBar from '../../../components/SideBar';
import axiosInstance from '../../../variables/api/axiosInstance';
import { displayNumbers } from '../../../variables/numbersFunctions';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';

dayjs.locale('fr');

const STATUT_CONFIG = {
    BROUILLON: { color: 'orange',  label: 'Brouillon',  icon: <ClockCircleOutlined /> },
    VALIDE:    { color: 'blue',    label: 'Validé',     icon: <CheckCircleOutlined /> },
    PAYE:      { color: 'green',   label: 'Payé',       icon: <DollarOutlined /> },
};

const ArretesVT = () => {
    const [arretes, setArretes]               = useState([]);
    const [loading, setLoading]               = useState(true);
    const [brouillonVisible, setBrouillonVisible] = useState(false);
    const [apercu, setApercu]                 = useState(null);
    const [apercuLoading, setApercuLoading]   = useState(false);
    const [creerLoading, setCreerLoading]     = useState(false);
    const [selectedMois, setSelectedMois]     = useState(dayjs().startOf('month'));
    const [form]                              = Form.useForm();
    const navigate                            = useNavigate();

    useEffect(() => { chargerArretes(); }, []);

    const chargerArretes = async () => {
        setLoading(true);
        try {
            const res = await axiosInstance.get('/api/vt/arretes');
            setArretes(res.data);
        } catch {
            toast.error('Erreur lors du chargement des arrêtés');
        } finally {
            setLoading(false);
        }
    };

    const ouvrirModalBrouillon = async () => {
        form.resetFields();
        setApercu(null);
        setBrouillonVisible(true);
        // Charger l'aperçu calculé pour le mois sélectionné
        await chargerApercu(selectedMois);
    };

    const chargerApercu = async (mois) => {
        setApercuLoading(true);
        try {
            const res = await axiosInstance.get('/api/vt/arretes/preparer', {
                params: { mois: mois.format('YYYY-MM-DD') }
            });
            setApercu(res.data);
            // Pré-remplir le report avec la valeur calculée
            form.setFieldsValue({ reportMoisPrecedent: res.data.reportMoisPrecedent });
        } catch {
            toast.error('Erreur lors du calcul de l\'aperçu');
        } finally {
            setApercuLoading(false);
        }
    };

    const creerBrouillon = async () => {
        try {
            const values = await form.validateFields();
            setCreerLoading(true);
            await axiosInstance.post('/api/vt/arretes', {
                moisArrete:             selectedMois.format('YYYY-MM-DD'),
                chargesExceptionnelles: values.chargesExceptionnelles || 0,
                reportMoisPrecedent:    values.reportMoisPrecedent || 0,
            });
            toast.success('Arrêté créé en brouillon');
            setBrouillonVisible(false);
            chargerArretes();
        } catch {
            toast.error('Erreur lors de la création de l\'arrêté');
        } finally {
            setCreerLoading(false);
        }
    };

    const columns = [
        {
            title: 'Mois',
            dataIndex: 'moisArrete',
            key: 'mois',
            render: (d) => dayjs(d).format('MMMM YYYY').replace(/^\w/, c => c.toUpperCase()),
            sorter: (a, b) => dayjs(a.moisArrete).unix() - dayjs(b.moisArrete).unix(),
            defaultSortOrder: 'descend',
        },
        {
            title: 'Statut',
            dataIndex: 'statut',
            key: 'statut',
            render: (s) => {
                const cfg = STATUT_CONFIG[s] || { color: 'default', label: s };
                return <Tag color={cfg.color} icon={cfg.icon}>{cfg.label}</Tag>;
            },
        },
        {
            title: 'Nb VL',
            dataIndex: 'nbVL',
            align: 'right',
            render: (v) => (v || 0).toLocaleString('fr-FR'),
        },
        {
            title: 'Nb Camions',
            dataIndex: 'nbCamions',
            align: 'right',
            render: (v) => (v || 0).toLocaleString('fr-FR'),
        },
        {
            title: 'Primes nettes (FCFA)',
            dataIndex: 'primesNettesTotal',
            align: 'right',
            render: (v) => displayNumbers(v),
        },
        {
            title: 'Commission STAC',
            dataIndex: 'commissionSTAC',
            align: 'right',
            render: (v) => displayNumbers(v),
        },
        {
            title: 'Actions',
            key: 'actions',
            align: 'center',
            render: (_, record) => (
                <Button
                    type="link"
                    icon={<EyeOutlined />}
                    onClick={() => navigate(`/retrocession-vt/arrete/${record.arreteId}`)}
                >
                    Détail
                </Button>
            ),
        },
    ];

    const nbBrouillons = arretes.filter(a => a.statut === 'BROUILLON').length;
    const nbValides    = arretes.filter(a => a.statut === 'VALIDE').length;
    const nbPayes      = arretes.filter(a => a.statut === 'PAYE').length;

    return (
        <ConfigProvider locale={frFR}>
            <div className="layout-wrapper layout-content-navbar">
                <div className="layout-container">
                <SideBar currentMenu="op-retro-vt" />
                <div className="layout-page">
                    <NavBar />
                    <div className="content-wrapper">
                        <div className="container-xxl flex-grow-1 container-p-y">
                            <div className="d-sm-flex align-items-center justify-content-between mb-4">
                                <h1 className="h3 mb-0 text-gray-800">
                                    <FileTextOutlined /> Arrêtés de production VT
                                </h1>
                                <Button
                                    type="primary"
                                    icon={<PlusOutlined />}
                                    onClick={ouvrirModalBrouillon}
                                >
                                    Préparer un arrêté
                                </Button>
                            </div>

                            {/* Compteurs */}
                            <Row gutter={16} style={{ marginBottom: 16 }}>
                                <Col xs={24} md={8}>
                                    <Card>
                                        <Statistic title="En brouillon" value={nbBrouillons}
                                            valueStyle={{ color: '#fa8c16' }} />
                                    </Card>
                                </Col>
                                <Col xs={24} md={8}>
                                    <Card>
                                        <Statistic title="Validés" value={nbValides}
                                            valueStyle={{ color: '#1677ff' }} />
                                    </Card>
                                </Col>
                                <Col xs={24} md={8}>
                                    <Card>
                                        <Statistic title="Payés" value={nbPayes}
                                            valueStyle={{ color: '#52c41a' }} />
                                    </Card>
                                </Col>
                            </Row>

                            {/* Tableau */}
                            <Card title="Historique des arrêtés VT">
                                <Table
                                    dataSource={arretes}
                                    columns={columns}
                                    rowKey="arreteId"
                                    loading={loading}
                                    pagination={{ pageSize: 12, showSizeChanger: false }}
                                    size="small"
                                    onRow={(record) => ({
                                        style: { cursor: 'pointer' },
                                        onClick: () => navigate(`/retrocession-vt/arrete/${record.arreteId}`),
                                    })}
                                />
                            </Card>
                        </div>
                    </div>
                    <Footer />
                </div>
                </div>
            </div>

            {/* Modal : créer un brouillon */}
            <Modal
                title="Préparer un arrêté VT"
                open={brouillonVisible}
                onCancel={() => setBrouillonVisible(false)}
                width={680}
                footer={[
                    <Button key="cancel" onClick={() => setBrouillonVisible(false)}>Annuler</Button>,
                    <Button
                        key="create"
                        type="primary"
                        loading={creerLoading}
                        onClick={creerBrouillon}
                    >
                        Créer le brouillon
                    </Button>,
                ]}
            >
                {/* Sélecteur de mois */}
                <Row gutter={16} align="middle" style={{ marginBottom: 16 }}>
                    <Col><strong>Mois de l'arrêté :</strong></Col>
                    <Col>
                        <DatePicker
                            picker="month"
                            value={selectedMois}
                            format="MMMM YYYY"
                            allowClear={false}
                            onChange={(val) => {
                                const m = val?.startOf('month');
                                setSelectedMois(m);
                                chargerApercu(m);
                            }}
                        />
                    </Col>
                </Row>

                {/* Aperçu calculé */}
                {apercuLoading ? (
                    <div style={{ textAlign: 'center', padding: 32 }}><Spin /></div>
                ) : apercu ? (
                    <>
                        <Alert
                            type="info"
                            showIcon
                            style={{ marginBottom: 16 }}
                            message="Agrégats calculés sur les règlements VT non encore arrêtés"
                        />
                        <Descriptions bordered size="small" column={2} style={{ marginBottom: 16 }}>
                            <Descriptions.Item label="VL">{(apercu.nbVL || 0).toLocaleString('fr-FR')}</Descriptions.Item>
                            <Descriptions.Item label="Camions">{(apercu.nbCamions || 0).toLocaleString('fr-FR')}</Descriptions.Item>
                            <Descriptions.Item label="Primes TTC">{displayNumbers(apercu.primesTTCTotal)} F</Descriptions.Item>
                            <Descriptions.Item label="Primes nettes HT">{displayNumbers(apercu.primesNettesTotal)} F</Descriptions.Item>
                            <Descriptions.Item label="Taxes (6%)">{displayNumbers(apercu.taxesTotal)} F</Descriptions.Item>
                            <Descriptions.Item label="Commission STAC (20%)">{displayNumbers(apercu.commissionSTAC)} F</Descriptions.Item>
                            <Descriptions.Item label="Transitaires">{displayNumbers(apercu.commissionTransitaires)} F</Descriptions.Item>
                            <Descriptions.Item label="Prévention routière">{displayNumbers(apercu.preventionRoutiere)} F</Descriptions.Item>
                            <Descriptions.Item label="Versements bancaires">{displayNumbers(apercu.totalVersementsBancaires)} F</Descriptions.Item>
                            <Descriptions.Item label="Report mois précédent">{displayNumbers(apercu.reportMoisPrecedent)} F</Descriptions.Item>
                            <Descriptions.Item label="Sinistres payés ≥ oct. 2012">{displayNumbers(apercu.sinistresApresOct2012)} F</Descriptions.Item>
                            <Descriptions.Item label="Sinistres payés < oct. 2012">{displayNumbers(apercu.sinistresAvantOct2012)} F</Descriptions.Item>
                        </Descriptions>
                    </>
                ) : null}

                <Divider>Charges à saisir</Divider>

                <Form form={form} layout="vertical">
                    <Row gutter={16}>
                        <Col xs={24} md={12}>
                            <Form.Item name="chargesExceptionnelles" label="Charges exceptionnelles (FCFA)">
                                <InputNumber
                                    style={{ width: '100%' }}
                                    formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
                                    parser={(v) => v.replace(/\s/g, '')}
                                    min={0} defaultValue={0}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item name="reportMoisPrecedent" label="Report mois précédent (FCFA) — à confirmer">
                                <InputNumber
                                    style={{ width: '100%' }}
                                    formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
                                    parser={(v) => v.replace(/\s/g, '')}
                                    min={0}
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Modal>
        </ConfigProvider>
    );
};

export default ArretesVT;
